import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ChevronDown, Radio } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  useGetInvestigationForEdit,
  useUpdateInvestigation,
} from '@/application/services/investigation.service'
import {
  useGetEntityTranslationState,
  useGetInvestigationCategoryTerms,
} from '@/application/services/translation.service'
import { resolveLanguage, type SupportedLangCode } from '@/application/i18n/language'
import type {
  TranslationsWritePayload,
  CategoryLabelsWritePayload,
  TaxonomyTerm,
} from '@/domains/types'

import { Button } from '@/ui/components/common/Button'
import { SourceLanguageSelector } from '@/ui/components/translations/SourceLanguageSelector'
import { TranslationTabs } from '@/ui/components/translations/TranslationTabs'
import { AuthorSelector, type AuthorState } from '@/ui/components/forms/AuthorSelector'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Skeleton } from '@/ui/components/common/Skeleton'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/components/forms/Form'
import { ImageField } from '@/ui/components/forms/ImageField'
import { Input } from '@/ui/components/forms/Input'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { TaxonomyTermPicker } from '@/ui/components/forms/TaxonomyTermPicker'
import { TermLabelsEditor } from '@/ui/components/translations/TermLabelsEditor'
import { InvestigationPublicUrls } from './InvestigationPublicUrls'

// ─── Schema ───────────────────────────────────────────────────────────────────

const makeUpdateInvestigationSchema = (
  translate: (k: string, opts?: Record<string, unknown>) => string,
) =>
  z.object({
    name: z
      .string()
      .min(2, translate('validation.name_min'))
      .max(250, translate('validation.name_max')),
    description: z.string().max(20000, translate('validation.desc_max')),
    investigationId: z
      .string()
      .min(2, translate('validation.id_min'))
      .max(200, translate('validation.id_max')),
    imageUrl: z
      .string()
      .max(2048)
      .refine(
        (value) =>
          !value ||
          value.startsWith('/') ||
          (() => {
            try {
              new URL(value)
              return true
            } catch {
              return false
            }
          })(),
        { message: translate('validation.image_url') },
      )
      .optional()
      .or(z.literal('')),
    liveDate: z.string().optional().nullable(),
    twitchUrl: z
      .string()
      .url(translate('validation.url_invalid'))
      .max(2048)
      .optional()
      .nullable()
      .or(z.literal('')),
    youtubeUrl: z
      .string()
      .url(translate('validation.url_invalid'))
      .max(2048)
      .optional()
      .nullable()
      .or(z.literal('')),
  })

type UpdateInvestigationValues = z.infer<ReturnType<typeof makeUpdateInvestigationSchema>>

// ─── Section ──────────────────────────────────────────────────────────────────

interface EditInvestigationFormSectionProps {
  investigationId: string
}

/**
 * Owns the entire edit-investigation experience:
 * loads the investigation by id, hydrates the React-Hook-Form state, manages
 * the author selector (which lives outside RHF because it controls two
 * correlated fields), renders the form card with all its fields, and submits
 * the update through the investigation service before navigating back to the
 * list. Also renders its own loading skeleton while the investigation loads.
 */
export function EditInvestigationFormSection({
  investigationId,
}: EditInvestigationFormSectionProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { data: investigation, isLoading } = useGetInvestigationForEdit(investigationId)
  const { mutateAsync: updateInvestigation, isPending } = useUpdateInvestigation()

  // Author is managed outside RHF because it involves two correlated fields
  const [liveSectionOpen, setLiveSectionOpen] = useState(false)
  const [author, setAuthor] = useState<AuthorState>({ id: null, name: '', username: null })

  // Source language + translation tabs (TRANSLATION-PLAN.md §5) — also
  // managed outside RHF, since they write to a different destination
  // (translation rows, not the content row) and the selector is only
  // editable while zero translation rows exist (N2).
  const [sourceLocale, setSourceLocale] = useState<SupportedLangCode>('fr')
  const [translations, setTranslations] = useState<TranslationsWritePayload>({})
  const { data: entityState } = useGetEntityTranslationState('investigation', investigationId)

  // Category terms (taxonomy): the linked terms come from a locale-free
  // endpoint, never from the `?lang=` investigation read, so no translated text
  // can leak into the source fields. The links are managed by the source tab's
  // picker; the other tabs only type labels, kept here as `{ locale: { termId:
  // label } }` with ONLY the values the user edited.
  const { data: loadedCategoryTerms } = useGetInvestigationCategoryTerms(investigationId)
  const [categoryTerms, setCategoryTerms] = useState<TaxonomyTerm[]>([])
  const [categoryTermsLoaded, setCategoryTermsLoaded] = useState(false)
  const [categoryLabelEdits, setCategoryLabelEdits] = useState<
    Record<string, Record<string, string>>
  >({})
  useEffect(() => {
    // Hydrate once: a later refetch must not overwrite the user's edits.
    if (loadedCategoryTerms && !categoryTermsLoaded) {
      setCategoryTerms(loadedCategoryTerms)
      setCategoryTermsLoaded(true)
    }
  }, [loadedCategoryTerms, categoryTermsLoaded])
  const hasExistingTranslations = (entityState ?? []).some(
    (localeState) => localeState.missingFields < 2,
  )

  const schema = useMemo(() => makeUpdateInvestigationSchema(t), [t])
  const foreignTerms = categoryTerms.filter((term) => term.sourceLocale !== sourceLocale)

  const form = useForm<UpdateInvestigationValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      investigationId: '',
      imageUrl: '',
      liveDate: null,
      twitchUrl: '',
      youtubeUrl: '',
    },
  })

  const watchedLiveDate = form.watch('liveDate')

  // Set form values when investigation loads
  useEffect(() => {
    if (investigation) {
      form.reset({
        name: investigation.name,
        description: investigation.description,
        investigationId: investigation.investigationId,
        imageUrl: investigation.imageUrl ?? '',
        liveDate: investigation.liveDate
          ? (() => {
              const dateObj = new Date(investigation.liveDate!)
              const padNum = (numberVal: number) => String(numberVal).padStart(2, '0')
              return `${dateObj.getFullYear()}-${padNum(dateObj.getMonth() + 1)}-${padNum(dateObj.getDate())}T${padNum(dateObj.getHours())}:${padNum(dateObj.getMinutes())}`
            })()
          : null,
        twitchUrl: investigation.twitchUrl ?? '',
        youtubeUrl: investigation.youtubeUrl ?? '',
      })
      setAuthor({
        id: investigation.authorId ?? null,
        name: investigation.author ?? '',
        username: null,
      })
      setSourceLocale(resolveLanguage(investigation.sourceLocale ?? 'fr'))
    }
  }, [investigation, form])

  const setCategoryLabel = (locale: SupportedLangCode, termId: string, label: string) =>
    setCategoryLabelEdits((previous) => ({
      ...previous,
      [locale]: { ...previous[locale], [termId]: label },
    }))

  const handleSubmit = async (values: UpdateInvestigationValues) => {
    if (!investigationId) return
    // Only the edited labels, only for terms still linked and only where the
    // term has a label slot (never its own source locale); a blank input clears.
    const categoryLabels: CategoryLabelsWritePayload = {}
    for (const [locale, edits] of Object.entries(categoryLabelEdits)) {
      for (const [termId, label] of Object.entries(edits)) {
        const term = categoryTerms.find((candidate) => candidate.id === termId)
        if (!term || term.sourceLocale === locale) continue
        categoryLabels[locale] = { ...categoryLabels[locale], [termId]: label.trim() || null }
      }
    }
    try {
      await updateInvestigation({
        id: investigationId,
        data: {
          ...values,
          // Include both the display name and the UUID so the backend can
          // update both columns (author = display name, authorId = UUID).
          author: author.name || undefined,
          authorId: author.id,
          liveDate: values.liveDate ? new Date(values.liveDate).toISOString() : null,
          twitchUrl: values.twitchUrl || null,
          youtubeUrl: values.youtubeUrl || null,
          ...(hasExistingTranslations ? {} : { sourceLocale }),
          translations,
          // Never send an empty link list because the terms have not loaded.
          ...(categoryTermsLoaded ? { categoryTermIds: categoryTerms.map((term) => term.id) } : {}),
          ...(Object.keys(categoryLabels).length > 0 ? { categoryLabels } : {}),
        },
      })
      toast.success(t('admin.form_edit_success', { name: values.name }))
      navigate({ to: '/manage/investigations', search: { statusFilter: 'active' } })
    } catch (error: unknown) {
      toast.error(
        t('admin.form_edit_error', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  // ── Loading state ──
  if (isLoading) {
    return (
      <>
        <div className="flex items-center gap-4">
          <Link to="/manage/investigations" search={{ statusFilter: 'active' }}>
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
              {t('common.back')}
            </Button>
          </Link>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.form_edit_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.form_edit_title')}</CardTitle>
        <CardDescription>{t('admin.form_edit_subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Live stream */}
            <div className="rounded-lg border border-border">
              <button
                type="button"
                onClick={() => setLiveSectionOpen((open) => !open)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors rounded-lg"
              >
                <span className="flex items-center gap-2">
                  <Radio className="size-5 text-foreground" />
                  {t('admin.form_live_section')}
                  {watchedLiveDate && (
                    <span className="text-muted-foreground font-normal">
                      {'— '}
                      {new Date(watchedLiveDate).toLocaleString(i18n.language, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={`size-4 text-muted-foreground transition-transform duration-200 ${liveSectionOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {liveSectionOpen && (
                <div className="space-y-4 px-4 pb-4">
                  <FormField
                    control={form.control}
                    name="liveDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('admin.form_live_date_label')}</FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormDescription>{t('admin.form_live_date_desc')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="twitchUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('admin.form_live_twitch_label')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://twitch.tv/..."
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormDescription>{t('admin.form_live_twitch_desc')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="youtubeUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('admin.form_live_youtube_label')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://youtube.com/..."
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormDescription>{t('admin.form_live_youtube_desc')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            {investigation && (
              <InvestigationPublicUrls
                slugs={investigation.slugs}
                sourceLocale={investigation.sourceLocale}
              />
            )}

            {/* Author */}
            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('admin.form_author_label')}
                </label>
                <p className="text-sm text-muted-foreground mt-1">{t('admin.form_author_desc')}</p>
              </div>
              <AuthorSelector value={author} onChange={setAuthor} />
            </div>

            {/* Source language (TRANSLATION-PLAN.md §5 decision 1) */}
            <SourceLanguageSelector
              value={sourceLocale}
              onChange={setSourceLocale}
              disabled={hasExistingTranslations}
            />

            {/* Category terms (labels, non-gating) + Name/description (source tab) +
                language tabs (TRANSLATION-PLAN.md §5 decisions 2-6, §8) */}
            <TranslationTabs
              sourceLocale={sourceLocale}
              fields={[
                {
                  name: 'categories',
                  labelKey: 'admin.form_category_label',
                  kind: 'terms',
                  sourceValue: '',
                  terms: categoryTerms,
                },
                {
                  name: 'name',
                  labelKey: 'admin.form_name_label',
                  kind: 'input',
                  sourceValue: form.watch('name'),
                },
                {
                  name: 'description',
                  labelKey: 'admin.form_desc_label',
                  kind: 'richtext',
                  sourceValue: form.watch('description'),
                  richTextEditorProps: { scrollable: true, compact: false },
                },
              ]}
              entityState={entityState}
              onChange={setTranslations}
              categoryLabelValues={categoryLabelEdits}
              onCategoryLabelChange={setCategoryLabel}
              disabled={isPending}
              renderSourceFields={() => (
                <>
                  {/* Categories — moved above Name/description per explicit
                      design decision, so the entry point for tagging an
                      investigation is the first thing an editor sees. */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      {t('admin.form_category_label')}
                    </label>
                    <TaxonomyTermPicker
                      locale={sourceLocale}
                      value={categoryTerms}
                      onChange={setCategoryTerms}
                      placeholder={t('admin.form_category_placeholder')}
                      disabled={isPending}
                    />
                    <p className="text-sm text-muted-foreground">{t('admin.form_category_desc')}</p>
                    {/* Terms created in another language have no label in the source
                        language yet: the source tab is where it is typed. */}
                    {foreignTerms.length > 0 && (
                      <div className="space-y-2 rounded-md border border-border p-3">
                        <p className="text-sm font-medium">
                          {t('taxonomy.missing_source_labels_title', {
                            locale: sourceLocale.toUpperCase(),
                          })}
                        </p>
                        <TermLabelsEditor
                          terms={foreignTerms}
                          locale={sourceLocale}
                          referenceLocale={sourceLocale}
                          values={categoryLabelEdits[sourceLocale] ?? {}}
                          onChange={(termId, label) =>
                            setCategoryLabel(sourceLocale, termId, label)
                          }
                          disabled={isPending}
                        />
                      </div>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('admin.form_name_label')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('admin.form_name_placeholder')} {...field} />
                        </FormControl>
                        <FormDescription>{t('admin.form_name_desc')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('admin.form_desc_label')}</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('admin.form_desc_placeholder')}
                            disabled={isPending}
                            scrollable
                            onUploadError={(message) => toast.error(message)}
                          />
                        </FormControl>
                        <FormDescription>{t('admin.form_desc_helper')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            />

            {/* Investigation ID */}
            <FormField
              control={form.control}
              name="investigationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_id_label')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.form_id_placeholder')} {...field} />
                  </FormControl>
                  <FormDescription>{t('admin.form_id_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Cover image */}
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_image_label')}</FormLabel>
                  <FormControl>
                    <ImageField
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormDescription>{t('admin.form_image_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-end gap-2 pt-4">
              <Link to="/manage/investigations" search={{ statusFilter: 'active' }}>
                <Button variant="outline" type="button">
                  {t('admin.form_cancel')}
                </Button>
              </Link>
              <Button type="submit" isLoading={isPending}>
                {t('admin.form_edit_submit')}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
